import {
  decorateStreamLainDropdownPanel,
  getStreamLainDropdownMenuChromeCss,
  STREAMLAIN_DROPDOWN_MENU_PORTAL_Z_INDEX,
} from '@affine/core/components/dropdown-panel-chrome/chrome';
import { createLitPortal } from '@blocksuite/affine/components/portal';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { streamLainDisclosurePopoverShellStyles } from '@blocksuite/affine-shared/utils';
import { PlusIcon } from '@blocksuite/icons/lit';
import { css, html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import type { ChatChip, DocDisplayConfig } from '../ai-chat-chips';
import {
  getChatInputPortalContainer,
  getChatInputPortalOverlayWidth,
  getChatInputPortalReferenceElement,
  getChatInputPortalStyle,
} from '../ai-chat-chips/chat-input-portal-style';
import {
  streamLainDropdownMenuBoundaryWidthCss,
  streamLainDropdownMenuMiddleware,
} from '../ai-chat-input/dropdown-menu-chrome';
import type { SearchMenuConfig } from './type';

export class AIChatAddContext extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .ai-chat-add-context {
      display: flex;
      flex-shrink: 0;
      flex-grow: 0;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
  `;

  @property({ attribute: false })
  accessor docId: string | undefined;

  @property({ attribute: false })
  accessor independentMode: boolean | undefined;

  @property({ attribute: false })
  accessor addChip!: (chip: ChatChip) => Promise<void>;

  @property({ attribute: false })
  accessor addImages!: (images: File[]) => void;

  @property({ attribute: false })
  accessor docDisplayConfig!: DocDisplayConfig;

  @property({ attribute: false })
  accessor searchMenuConfig!: SearchMenuConfig;

  @property({ attribute: false })
  accessor portalContainer: HTMLElement | null = null;

  @query('.ai-chat-add-context')
  accessor addButton!: HTMLDivElement;

  private abortController: AbortController | null = null;

  private portalRoot: HTMLElement | null = null;

  override render() {
    return html`
      <div
        class="ai-chat-add-context"
        data-testid="chat-panel-with-button"
        @click=${this.toggleAddDocMenu}
      >
        ${PlusIcon()}
      </div>
    `;
  }

  private _getPortalElement(): HTMLElement | null {
    return (
      this.portalRoot?.shadowRoot?.querySelector<HTMLElement>(
        '.ai-chat-add-context-popover'
      ) ??
      this.portalRoot?.querySelector<HTMLElement>(
        '.ai-chat-add-context-popover'
      ) ??
      null
    );
  }

  private _decoratePortalElement(attempt = 0) {
    const portalElement = this._getPortalElement();
    if (!portalElement || !this.addButton) {
      if (attempt < 8) {
        requestAnimationFrame(() => this._decoratePortalElement(attempt + 1));
      }
      return;
    }

    decorateStreamLainDropdownPanel(portalElement, this.addButton);
  }

  private readonly toggleAddDocMenu = () => {
    if (this.abortController) {
      this.abortController.abort();
      return;
    }

    const boundary = getChatInputPortalReferenceElement(this, this.addButton);
    const portalWidth = getChatInputPortalOverlayWidth(boundary);

    this.abortController = new AbortController();
    this.abortController.signal.addEventListener('abort', () => {
      this.abortController = null;
      this.portalRoot = null;
    });

    const { portal } = createLitPortal({
      template: html`<style>
          ${getStreamLainDropdownMenuChromeCss()}
            ${streamLainDisclosurePopoverShellStyles}
            .ai-chat-add-context-popover {
            width: min(
              var(--streamlain-disclosure-overlay-width, 320px),
              ${streamLainDropdownMenuBoundaryWidthCss('320px')},
              calc(100vw - 32px)
            );
          }

          .ai-chat-add-context-popover chat-panel-add-popover {
            display: block;
            width: 100%;
            max-width: 100%;
          }
        </style>
        <span
          class="ai-chat-add-context-popover streamlain-disclosure-popover-shell"
          style=${styleMap(getChatInputPortalStyle(this, portalWidth))}
        >
          <chat-panel-add-popover
            .docId=${this.docId}
            .independentMode=${this.independentMode}
            .addChip=${this.addChip}
            .addImages=${this.addImages}
            .searchMenuConfig=${this.searchMenuConfig}
            .docDisplayConfig=${this.docDisplayConfig}
            .abortController=${this.abortController}
          ></chat-panel-add-popover>
        </span>`,
      portalStyles: {
        zIndex: STREAMLAIN_DROPDOWN_MENU_PORTAL_Z_INDEX,
      },
      container: getChatInputPortalContainer(this, this.portalContainer),
      computePosition: {
        referenceElement: this.addButton,
        placement: 'top-start',
        middleware: streamLainDropdownMenuMiddleware({
          horizontalBoundary: boundary,
        }),
        autoUpdate: { animationFrame: true },
      },
      abortController: this.abortController,
      closeOnClickAway: true,
    });
    this.portalRoot = portal;
    this._decoratePortalElement();
  };
}
